import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { useTranslation } from "react-i18next";
import Header from "@/components/Header";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Receipt, Smartphone, MessageSquare, Settings, Users, Bot, ShieldAlert, Percent, ArrowUpRight, ArrowDownLeft, MessageSquareText, SearchIcon, Server, CreditCard } from "lucide-react";
import AdminStats from "@/components/admin/AdminStats";
import AdminTransactions from "@/components/admin/AdminTransactions";
import AdminDevices from "@/components/admin/AdminDevices";
import AdminSupport from "@/components/admin/AdminSupport";
import AdminWalletSettings from "@/components/admin/AdminWalletSettings";
import AdminUsers from "@/components/admin/AdminUsers";
import AdminAIChat from "@/components/admin/AdminAIChat";
import AdminSecurityMonitor from "@/components/admin/AdminSecurityMonitor";
import AdminCommissionSettings from "@/components/admin/AdminCommissionSettings";
import AdminWithdrawals from "@/components/admin/AdminWithdrawals";
import AdminReceiveAddresses from "@/components/admin/AdminReceiveAddresses";
import AdminDeposits from "@/components/admin/AdminDeposits";
import AdminXPubSettings from "@/components/admin/AdminXPubSettings";
import AdminQuickReplies from "@/components/admin/AdminQuickReplies";
import AdminBinanceSettings from "@/components/admin/AdminBinanceSettings";
import AdminTransactionSearch from "@/components/admin/AdminTransactionSearch";
import AdminBlockchainBalances from "@/components/admin/AdminBlockchainBalances";
import AdminContaboVPS from "@/components/admin/AdminContaboVPS";
import AdminBinanceDiagnostics from "@/components/admin/AdminBinanceDiagnostics";
import AdminCardRedemptions from "@/components/admin/AdminCardRedemptions";


const Admin = () => {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: roleLoading } = useUserRole();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (!authLoading && !user) { navigate("/auth"); return; }
    if (!authLoading && !roleLoading && !isAdmin) { navigate("/dashboard"); return; }
  }, [user, authLoading, isAdmin, roleLoading, navigate]);

  if (authLoading || roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/3 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/3 w-[600px] h-[600px] bg-crypto-purple/5 rounded-full blur-3xl" />
      </div>

      <main className="container mx-auto px-2 sm:px-4 pt-20 sm:pt-24 pb-8 sm:pb-12 relative z-10">
        <div className="mb-4 sm:mb-8">
          <h1 className="text-xl sm:text-3xl font-bold text-foreground mb-1 sm:mb-2">{t('admin.title')}</h1>
          <p className="text-xs sm:text-base text-muted-foreground">{t('admin.subtitle')}</p>
        </div>

        <div className="mb-4 sm:mb-8"><AdminStats /></div>
        <div className="mb-4 sm:mb-8"><AdminBlockchainBalances /></div>

        <Tabs defaultValue="ai">
          <div className="overflow-x-auto -mx-2 px-2 mb-4 sm:mb-6">
            <TabsList className="bg-secondary inline-flex flex-nowrap sm:flex-wrap min-w-max sm:min-w-0 h-auto gap-1 p-1">
              <TabsTrigger value="search" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap"><SearchIcon className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" /><span className="hidden sm:inline">بحث</span><span className="sm:hidden">بحث</span></TabsTrigger>
              <TabsTrigger value="ai" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap"><Bot className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" /><span className="hidden sm:inline">{t('admin.aiAssistant')}</span><span className="sm:hidden">AI</span></TabsTrigger>
              <TabsTrigger value="security" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap"><ShieldAlert className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" /><span className="hidden sm:inline">{t('admin.security')}</span><span className="sm:hidden">أمان</span></TabsTrigger>
              <TabsTrigger value="users" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap"><Users className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" /><span className="hidden sm:inline">{t('admin.users')}</span><span className="sm:hidden">مستخدمين</span></TabsTrigger>
              <TabsTrigger value="transactions" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap"><Receipt className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" /><span className="hidden sm:inline">{t('admin.transactions')}</span><span className="sm:hidden">معاملات</span></TabsTrigger>
              <TabsTrigger value="devices" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap"><Smartphone className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" /><span className="hidden sm:inline">{t('admin.devices')}</span><span className="sm:hidden">أجهزة</span></TabsTrigger>
              <TabsTrigger value="support" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap"><MessageSquare className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" /><span className="hidden sm:inline">{t('admin.support')}</span><span className="sm:hidden">دعم</span></TabsTrigger>
              <TabsTrigger value="deposits" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap"><ArrowDownLeft className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" /><span className="hidden sm:inline">{t('admin.deposits')}</span><span className="sm:hidden">إيداع</span></TabsTrigger>
              <TabsTrigger value="withdrawals" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap"><ArrowUpRight className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" /><span className="hidden sm:inline">{t('admin.withdrawals')}</span><span className="sm:hidden">سحب</span></TabsTrigger>
              <TabsTrigger value="commission" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap"><Percent className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" /><span className="hidden sm:inline">{t('admin.commission')}</span><span className="sm:hidden">عمولة</span></TabsTrigger>
              <TabsTrigger value="quick-replies" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap"><MessageSquareText className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" /><span className="hidden sm:inline">الردود الجاهزة</span><span className="sm:hidden">ردود</span></TabsTrigger>
              <TabsTrigger value="card-redemptions" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap"><CreditCard className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" /><span className="hidden sm:inline">بطاقات yqqy</span><span className="sm:hidden">بطاقات</span></TabsTrigger>
              <TabsTrigger value="vps" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap"><Server className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" /><span className="hidden sm:inline">VPS</span><span className="sm:hidden">VPS</span></TabsTrigger>
              <TabsTrigger value="settings" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap"><Settings className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" /><span className="hidden sm:inline">{t('admin.settings')}</span><span className="sm:hidden">إعدادات</span></TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="ai"><AdminAIChat /></TabsContent>
          <TabsContent value="search"><AdminTransactionSearch /></TabsContent>
          <TabsContent value="security"><AdminSecurityMonitor /></TabsContent>
          <TabsContent value="users"><AdminUsers /></TabsContent>
          <TabsContent value="transactions"><AdminTransactions /></TabsContent>
          <TabsContent value="devices"><AdminDevices /></TabsContent>
          <TabsContent value="support"><AdminSupport /></TabsContent>
          <TabsContent value="commission"><AdminCommissionSettings /></TabsContent>
          <TabsContent value="deposits"><AdminDeposits /></TabsContent>
          <TabsContent value="withdrawals"><AdminWithdrawals /></TabsContent>
          <TabsContent value="quick-replies"><AdminQuickReplies /></TabsContent>
          <TabsContent value="card-redemptions"><AdminCardRedemptions /></TabsContent>
          <TabsContent value="vps">
            <div className="space-y-6">
              <AdminContaboVPS />
            </div>
          </TabsContent>
          <TabsContent value="settings">
            <div className="space-y-6">
              <AdminBinanceDiagnostics />
              <AdminXPubSettings />
              <AdminBinanceSettings />
              <AdminReceiveAddresses />
              <AdminWalletSettings />
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Admin;
